// admin/src/lib/keepSignedIn.ts
//
// "Sign in once, never again" for the admin site. Supabase keeps the
// session in localStorage, and the project never expires sessions
// (no timebox, no inactivity timeout), but browsers do throw that
// storage away: Safari deletes script-written storage for a site that
// hasn't been opened in 7 days, and clearing site data does the same.
// So the latest refresh token is also mirrored into an HttpOnly cookie
// that the Worker sets (src/worker.js, /auth/*), which those rules don't
// touch; when localStorage comes back empty, the token is read back from
// that cookie and the session is restored without a sign-in.
//
// The same module remembers the last page you were on, so opening the
// site (or signing in) lands you where you left off.

const LAST_PATH_KEY = 'calyard-admin-last-path';
const LAST_EMAIL_KEY = 'calyard-admin-last-email';
const DEFAULT_PATH = '/equipment';

/** Paths worth returning to: inside the admin shell, never the auth pages. */
export function isRestorablePath(path: string | null | undefined): path is string {
  if (!path || !path.startsWith('/') || path.startsWith('//')) return false;
  const pathname = path.split(/[?#]/)[0];
  if (pathname === '/' || pathname === '/login' || pathname === '/reset-password') return false;
  return true;
}

function read(key: string): string | null {
  try {
    return globalThis.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string) {
  try {
    globalThis.localStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: remembering is a convenience only.
  }
}

export function rememberPath(path: string) {
  if (isRestorablePath(path)) write(LAST_PATH_KEY, path);
}

export function lastPath(): string {
  const saved = read(LAST_PATH_KEY);
  return isRestorablePath(saved) ? saved : DEFAULT_PATH;
}

export function rememberEmail(email: string) {
  if (email.trim()) write(LAST_EMAIL_KEY, email.trim());
}

export function lastEmail(): string {
  return read(LAST_EMAIL_KEY) ?? '';
}

async function post(path: string, body?: unknown): Promise<Response | null> {
  try {
    return await fetch(path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    // No Worker in front (vite dev) or offline: nothing to mirror.
    return null;
  }
}

/** Mirror the current refresh token into the Worker's HttpOnly cookie. */
export async function keepRefreshToken(refreshToken: string): Promise<void> {
  if (!refreshToken) return;
  await post('/auth/keep', { refresh_token: refreshToken });
}

/** The refresh token the Worker kept, or null when there isn't one. */
export async function restoreRefreshToken(): Promise<string | null> {
  const res = await post('/auth/restore');
  if (!res || !res.ok) return null;
  try {
    const data = (await res.json()) as { refresh_token?: unknown };
    return typeof data.refresh_token === 'string' && data.refresh_token ? data.refresh_token : null;
  } catch {
    return null;
  }
}

/** Drop the kept token: only on an explicit "Sign out". */
export async function forgetRefreshToken(): Promise<void> {
  await post('/auth/forget');
}
